// Combo box: a text field you can type in freely, with a list under it to pick from. Typing narrows the list.
// Built by hand because the browser's own suggestion list (<datalist>) doesn't open inside dialogs on many
// Android phones. Markup: <div class="combo"> <input role="combobox"> <button class="combo-btn"> <ul class="combo-list">.
// Keys: ↓/↑ move, Enter picks, Esc closes.
import { el } from "./dom.ts";

export interface ComboGroup {
  /** A small heading above the group ("Recent"), if any. */
  label?: string;
  items: string[];
}
export interface ComboSetup {
  input: HTMLInputElement;
  button: HTMLButtonElement;
  list: HTMLUListElement;
  /** What to show for the typed text ("" = everything). */
  options: (query: string) => ComboGroup[];
  /** Shown when nothing matches. */
  emptyText: () => string;
}

export function combo({ input, button, list, options, emptyText }: ComboSetup): { close: () => void; destroy: () => void } {
  let items: HTMLLIElement[] = [], active = -1;
  const isOpen = () => !list.hidden;

  function setActive(i: number): void {
    items[active]?.setAttribute("aria-selected", "false");
    active = items.length ? (i + items.length) % items.length : -1;
    const li = items[active];
    if (li) { li.setAttribute("aria-selected", "true"); li.scrollIntoView({ block: "nearest" }); input.setAttribute("aria-activedescendant", li.id); }
    else input.removeAttribute("aria-activedescendant");
  }
  /** The item's text with the typed part in bold. */
  function withMatch(text: string, q: string): (Node | string)[] {
    const i = q ? text.toLowerCase().indexOf(q) : -1;
    return i < 0 ? [text] : [text.slice(0, i), el("mark", { text: text.slice(i, i + q.length) }), text.slice(i + q.length)];
  }

  function open(): void {
    const typed = input.value.trim(), q = typed.toLowerCase();
    // A name that is already complete (just picked, or an existing catch) shows the whole list, not only itself.
    const all = options("");
    const complete = all.some((g) => g.items.some((s) => s.toLowerCase() === q));
    const groups = !q || complete ? all : options(typed);
    const rows: HTMLLIElement[] = [];
    items = [];
    let n = 0;
    for (const g of groups) {
      if (!g.items.length) continue;
      if (g.label) rows.push(el("li", { class: "combo-head", role: "presentation", text: g.label }));
      for (const s of g.items) {
        const li = el("li", { role: "option", id: `${list.id}-${n++}`, "aria-selected": "false" }, ...withMatch(s, complete ? "" : q));
        li.addEventListener("pointerdown", (e) => e.preventDefault()); // keep the keyboard open while picking
        li.addEventListener("click", () => pick(s));
        rows.push(li);
        items.push(li);
      }
    }
    if (!items.length) rows.push(el("li", { class: "combo-note", role: "presentation", text: emptyText() }));
    list.replaceChildren(...rows);
    list.hidden = false;
    input.setAttribute("aria-expanded", "true");
    active = -1;
    // the current name, highlighted, when the whole list is shown
    const current = complete ? items.findIndex((li) => li.textContent?.toLowerCase() === q) : -1;
    if (current >= 0) setActive(current);
    list.scrollIntoView({ block: "nearest" });
  }
  function close(): void {
    if (!isOpen()) return;
    list.hidden = true;
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
  }
  function pick(s: string): void {
    input.value = s;
    close();
  }

  input.addEventListener("input", open);
  input.addEventListener("click", () => { if (!isOpen()) open(); });
  input.addEventListener("keydown", (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!isOpen()) open();
      setActive(active + (e.key === "ArrowDown" ? 1 : -1));
    } else if (e.key === "Enter" && isOpen() && active >= 0) {
      e.preventDefault(); // pick, don't submit the form
      pick(items[active].textContent ?? "");
    } else if (e.key === "Escape" && isOpen()) {
      e.preventDefault(); // close the list, not the dialog
      e.stopPropagation();
      close();
    }
  });
  // the arrow opens the list without bringing up the phone's keyboard
  button.addEventListener("pointerdown", (e) => e.preventDefault());
  button.addEventListener("click", () => { if (isOpen()) close(); else open(); });
  // tapping anywhere else, or tabbing away, closes it
  const box = input.parentElement!;
  const outside = (e: PointerEvent) => { if (isOpen() && !box.contains(e.target as Node)) close(); };
  document.addEventListener("pointerdown", outside);
  box.addEventListener("focusout", (e) => { if (!box.contains(e.relatedTarget as Node | null)) close(); });
  // destroy(): for combo boxes made inside a sheet, when the sheet closes
  return { close, destroy: () => document.removeEventListener("pointerdown", outside) };
}
